import React, { useState } from 'react';
import { CheckIcon, RotateCcwIcon, XIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Meter } from '../ui/Meter';
import type { Quiz } from '../../types/learning';
interface QuizRunnerProps {
  quiz: Quiz;
  onExit: () => void;
}
export function QuizRunner({
  quiz,
  onExit
}: QuizRunnerProps) {
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [submitted, setSubmitted] = useState(false);
  const question = quiz.questions[index];
  const answered = Object.keys(answers).length;
  const correct = quiz.questions.filter(item => answers[item.id] === item.correctIndex).length;
  const score = Math.round(correct / quiz.questions.length * 100);
  function restart() {
    setAnswers({});
    setSubmitted(false);
    setIndex(0);
  }
  return <div className="rounded-card border border-line bg-surface" data-id="element-2907" data-mp="1hwk9o2yaam6s">
      <header className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-4" data-id="element-2908" data-mp="1hyeu7g5n8cac">
        <div className="min-w-0" data-id="element-2909" data-mp="2zwnlne8aaf7m">
          <div className="flex flex-wrap items-center gap-2" data-id="element-2910" data-mp="34xjnlm92rrae">
            <h3 className="text-[15px] font-semibold text-ink" data-id="element-2911" data-mp="34vqs933wwthp">{quiz.title}</h3>
            <Badge tone={quiz.scored ? 'brand' : 'neutral'} data-id="element-2912" data-mp="2q012ctvlvw7p">
              {quiz.scored ? 'Scored' : 'Practice — unscored'}
            </Badge>
          </div>
          <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-2913" data-mp="2h7pd3bc8aetb">
            {quiz.questions.length} questions ·{' '}
            {quiz.attemptsAllowed === null ? 'unlimited attempts' : `attempt ${Math.min(quiz.attemptsUsed + 1, quiz.attemptsAllowed)} of ${quiz.attemptsAllowed}`}
            {quiz.dueLabel ? ` · ${quiz.dueLabel}` : ''}
          </p>
        </div>
        <Button size="sm" variant="ghost" className="ml-auto" onClick={onExit} data-id="element-2914" data-mp="1y9mvvipx9m62">
          Close
        </Button>
      </header>

      {!submitted ? <div className="px-5 py-4" data-id="element-2915" data-mp="22a6qaej42boi">
          <Meter label={`Question ${index + 1} of ${quiz.questions.length}`} value={(index + 1) / quiz.questions.length * 100} trailing={`${answered} answered`} height="thin" data-id="element-2916" data-mp="8s9826sczrwx" />

          <fieldset className="mt-4" data-id="element-2917" data-mp="3v8vhx3199sfi">
            <legend className="text-[14px] font-medium leading-6 text-ink" data-id="element-2918" data-mp="3v5r91scui8ii">{question.prompt}</legend>
            <ul className="mt-3 space-y-2" data-id="element-2919" data-mp="3q6o407wv82aj">
              {question.options.map((option, optionIndex) => {
            const selected = answers[question.id] === optionIndex;
            return <li key={option} data-id="element-2920" data-mp="mq7jp4fapa8q" data-mpi={optionIndex}>
                    <label className={`flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 transition-colors duration-150 ease-out ${selected ? 'border-brand-700 bg-brand-50' : 'border-line-strong hover:border-ink-subtle'}`} data-id="element-2921" data-mp="hs5yacuecyu3">
                      <input type="radio" name={question.id} checked={selected} onChange={() => setAnswers(current => ({
                  ...current,
                  [question.id]: optionIndex
                }))} className="mt-0.5 h-4 w-4 shrink-0 accent-brand-700" data-id="element-2922" data-mp="1e02a84tispju" />
                      <span className="text-[13px] leading-6 text-ink" data-id="element-2923" data-mp="1wqteirynhprd">{option}</span>
                    </label>
                  </li>;
          })}
            </ul>
          </fieldset>

          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-line pt-4" data-id="element-2924" data-mp="25rrm1k8asfep">
            <Button size="sm" variant="secondary" disabled={index === 0} onClick={() => setIndex(value => value - 1)} data-id="element-2925" data-mp="2aq1idy5rdm1w">
              Previous
            </Button>
            {index < quiz.questions.length - 1 ? <Button size="sm" variant="secondary" onClick={() => setIndex(value => value + 1)} data-id="element-2926" data-mp="3h6ge9kncuobl">
                Next
              </Button> : <Button size="sm" variant="primary" disabled={answered < quiz.questions.length} onClick={() => setSubmitted(true)} data-id="element-2927" data-mp="3c9zflwij4rk9">
                Submit answers
              </Button>}
            {answered < quiz.questions.length && index === quiz.questions.length - 1 && <span className="text-[12px] text-ink-muted" data-id="element-2928" data-mp="2tiq3dfhdjooz">
                {quiz.questions.length - answered} question
                {quiz.questions.length - answered === 1 ? '' : 's'} still unanswered
              </span>}
          </div>
        </div> : <div className="px-5 py-4" data-id="element-2929" data-mp="2fp77i17lbe5i">
          <div className="flex flex-wrap items-center gap-3" data-id="element-2930" data-mp="2fo5y01682zpw">
            <p className="cz-num text-3xl font-semibold text-ink" data-id="element-2931" data-mp="2arp5yvvwk17j">
              {quiz.scored ? `${score}%` : `${correct}/${quiz.questions.length}`}
            </p>
            <div data-id="element-2932" data-mp="uzldr9se67r9">
              <p className="text-[13px] font-medium text-ink" data-id="element-2933" data-mp="v03iyvx17wbb">
                {correct} of {quiz.questions.length} correct
              </p>
              <p className="text-[12px] text-ink-muted" data-id="element-2934" data-mp="gca2za41ars7">
                {quiz.scored ? 'Recorded as Campuzon learning progress — this is not a university grade.' : 'Practice attempts are never recorded against you.'}
              </p>
            </div>
          </div>

          <ul className="mt-4 divide-y divide-line border-t border-line" data-id="element-2935" data-mp="1dxo720rz2quv">
            {quiz.questions.map((item, questionIndex) => {
          const chosen = answers[item.id];
          const right = chosen === item.correctIndex;
          return <li key={item.id} className="py-3.5" data-id="element-2936" data-mp="3konkwn2qjstx" data-mpi={questionIndex}>
                  <div className="flex items-start gap-2" data-id="element-2937" data-mp="3kr9t8oxgacd7">
                    <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${right ? 'bg-ok-700' : 'bg-warn-700'}`} data-id="element-2938" data-mp="3ora6w7cq6jj7">
                      {right ? <CheckIcon className="h-3 w-3 text-white" data-id="element-2939" data-mp="1lh6xgs3jpmdh" /> : <XIcon className="h-3 w-3 text-white" data-id="element-2940" data-mp="1gjnj7s6ul9lj" />}
                    </span>
                    <div className="min-w-0" data-id="element-2941" data-mp="1ciboj8949y6h">
                      <p className="text-[13px] font-medium text-ink" data-id="element-2942" data-mp="1cexnc3c6vv0m">
                        {questionIndex + 1}. {item.prompt}
                      </p>
                      <p className="mt-1 text-[12px] text-ink-muted" data-id="element-2943" data-mp="12k5s3662xk90">
                        Your answer: {item.options[chosen] ?? 'none'}
                      </p>
                      {!right && <p className="mt-0.5 text-[12px] text-ink-muted" data-id="element-2944" data-mp="2wvu8zdvvfww1">
                          Correct answer: {item.options[item.correctIndex]}
                        </p>}
                      <p className="mt-1 text-[12px] leading-5 text-ink-subtle" data-id="element-2945" data-mp="30e9uueuukisq">{item.feedback}</p>
                    </div>
                  </div>
                </li>;
        })}
          </ul>

          <div className="mt-4 flex flex-wrap gap-2 border-t border-line pt-4" data-id="element-2946" data-mp="397wljqk76vv2">
            <Button size="sm" variant="secondary" icon={<RotateCcwIcon className="h-3.5 w-3.5" data-id="element-2948" data-mp="2hgywqxkybvev" />} onClick={restart} disabled={quiz.attemptsAllowed !== null && quiz.attemptsUsed + 1 >= quiz.attemptsAllowed} data-id="element-2947" data-mp="3e5eon0bjagv5">
              {quiz.attemptsAllowed === null ? 'Try again' : 'Use remaining attempt'}
            </Button>
            <Button size="sm" variant="ghost" onClick={onExit} data-id="element-2949" data-mp="2vh9khrqystvt">
              Back to quizzes
            </Button>
          </div>
        </div>}
    </div>;
}